/**
 * `transactionType` de la pre-transacción "entre cuentas propias" (misma moneda, sin exchange —
 * ver el JSDoc de `useBetweenAccountsQuoter`). Acordado con backend en ML-13 (2026-09-14):
 * `P2P_ADDITIONAL_ACCOUNT`. `EXCHANGE_ADDITIONAL` pasó a ser el `transactionType` de la
 * pantalla de exchange (cuando el exchange involucra una cuenta adicional), no de esta.
 *
 * El valor existe en `enums/TransactionTypeEnum.java` de ms-wallet (medido contra `master` el
 * 2026-09-21). **Ojo: ese repo tiene DOS enums**, y `b2b/model/enums/TransactionType.java` llega
 * hasta `MONEY_DESK` sin ninguno de los `_ADDITIONAL`. El que valida es el primero — lo prueba
 * que ML-57 devolvió un 400 de negocio y no un fallo de deserialización. Lo que falta medir es
 * el deploy en `ci`: `master` y el ambiente son dos cosas.
 */
export const TRANSACTION_TYPE_BETWEEN_ACCOUNTS = 'P2P_ADDITIONAL_ACCOUNT';

/**
 * Mínimo de cuentas que una moneda necesita para ofrecer un destino: con la regla de misma
 * divisa de `filterDestinationAccounts`, una moneda con una sola cuenta no tiene ningún candidato
 * posible (sería la propia cuenta de origen).
 */
export const MIN_ACCOUNTS_FOR_ELIGIBLE_CURRENCY = 2;

/** Copy de la vista `BetweenAccountsQuoter` (Figma B2B_Wallets `QV8OodSHqhZbgH1SkaPTQa`, nodo `1885:31852`). */
export const BETWEEN_ACCOUNTS_QUOTER_TITLE = '¿Cuánto dinero quieres traspasar?';
export const BETWEEN_ACCOUNTS_QUOTER_SUBTITLE =
  'Traspasa y recibe fondos entre tus cuentas.';
export const BETWEEN_ACCOUNTS_AVAILABLE_LABEL = 'Disponible';
export const BETWEEN_ACCOUNTS_SEND_ALL_LABEL = 'Enviar todo';
export const BETWEEN_ACCOUNTS_FROM_LABEL = 'Traspasas';
export const BETWEEN_ACCOUNTS_TO_LABEL = 'Recibes';
export const BETWEEN_ACCOUNTS_INSTANT_TRANSFER_NOTICE = 'Tu dinero llega al instante';

/**
 * Copy del empty state (Figma nodo `4233:10715`) que se muestra en vez del cotizador cuando
 * ninguna moneda del usuario tiene dos o más cuentas: con la regla de misma divisa
 * (`filterDestinationAccounts`), esa moneda no tiene destino posible y `GQuote` esconde sus
 * selectores sin avisar — mejor este mensaje explícito que la pantalla abriendo sin ninguna salida.
 */
export const BETWEEN_ACCOUNTS_NO_ELIGIBLE_ACCOUNTS_MESSAGE =
  'Debes tener al menos dos cuentas en la misma moneda para poder transferir entre ellas.';
export const BETWEEN_ACCOUNTS_CREATE_ACCOUNT_LABEL = 'Crear cuenta';
export const BETWEEN_ACCOUNTS_BACK_LABEL = 'Volver';

/**
 * Acompaña al error de carga del catálogo. Un fallo de red deja los selectores igual de vacíos que
 * el caso de arriba, así que sin este camino la pantalla le afirmaría al usuario que no tiene dos
 * cuentas en la misma moneda, que puede ser falso — y sin forma de volver a intentar.
 */
export const BETWEEN_ACCOUNTS_RETRY_LABEL = 'Reintentar';

/**
 * Acompaña el fallo de `walletStore.setWallets()`. A diferencia del error del catálogo —que
 * trae su propio mensaje traducido del `reason` del backend vía `getReasonMessage`—
 * `setWallets()` solo informa éxito o fracaso con un booleano (`ApiFailure` no sale de esa
 * acción), así que este copy es fijo. Comparte la misma rama de error con reintento que
 * `BETWEEN_ACCOUNTS_RETRY_LABEL`: un fallo de `/wallets` deja los selectores tan vacíos como uno
 * de catálogo, y sin este mensaje la pantalla caería en el empty state y afirmaría algo falso
 * sobre la empresa del usuario.
 */
export const BETWEEN_ACCOUNTS_WALLETS_ERROR_MESSAGE =
  'No pudimos cargar tus cuentas. Vuelve a intentarlo en unos minutos.';

/**
 * `product` que espera el 2FA (`auth-server/b2b/two-factor/${product}`, ver
 * `twoFactorAuth.service.ts`) — taxonomía DISTINTA del `transactionType` de la pre-transacción
 * (`TRANSACTION_TYPE_BETWEEN_ACCOUNTS`, arriba). `'P2P'` ya se usa como `product` en el repo
 * (`useP2pSummary.ts`, `useConfirmation.ts`), no es un valor nuevo. Los `product` reales son
 * `'REMITTANCE'`, `'EXCHANGE'`, `'P2P'`, `'INVITATION'`, `'ADMIN_UPDATE_USER'` y
 * `'MULTI_TRANSACTION'` — `'REMITTANCE_B2B'` NO es uno: solo es `product` de `createQuote`.
 */
export const TWO_FACTOR_PRODUCT_BETWEEN_ACCOUNTS = 'P2P';

/**
 * Producto con el que se cotiza y con el que se resuelven las rutas de esta pantalla. Es una
 * transferencia entre cuentas propias, así que va por los mismos rieles que `P2pQuoter`
 * (`getRoutesByProduct` y `createQuote` en `useP2pQuoter`) y no por los de exchange.
 *
 * Lo que cambia de verdad es el `product` que se le manda a `quote/b2b`. El filtro de rutas hoy
 * da el mismo `id`: medido en `ci` sobre `route/b2b/routes`, una ruta se lista una vez por cada
 * producto que soporta —la de COP (`id` 289) aparece en nueve— así que las 14 monedas con ruta
 * de wallet tienen `id` idéntico bajo `'EXCHANGE'` y bajo `'P2P'`. El filtro igual va por `'P2P'`
 * y no por herencia: si backend habilita una moneda para exchange y no para transferencia entre
 * cuentas, este filtro lo respeta y el de exchange la dejaría pasar.
 *
 * Tercera taxonomía del archivo, y no colapsa con las otras dos: el `transactionType` de la
 * pre-transacción es `TRANSACTION_TYPE_BETWEEN_ACCOUNTS` y el `product` del 2FA es
 * `TWO_FACTOR_PRODUCT_BETWEEN_ACCOUNTS`. Que este valor coincida con el del 2FA es una
 * coincidencia de valor, no una relación: son campos de APIs distintas.
 */
export const QUOTE_PRODUCT_BETWEEN_ACCOUNTS = 'P2P' as const;

/**
 * `reason` de la pre-transacción que significan "el PIN u OTP que escribiste está mal" y no "la
 * operación falló": el 2FA se queda abierto con el error para reintentar. `ATTEMPT_LIMIT_EXCEEDED`
 * queda fuera a propósito: ahí ya no hay reintento posible y el flujo se cierra.
 */
export const BETWEEN_ACCOUNTS_RETRYABLE_2FA_REASONS: ReadonlySet<string> = new Set([
  'PIN_FAILED_ATTEMPT_ONE',
  'PIN_FAILED_ATTEMPT_TWO',
  'PIN_FAILED_ATTEMPT_THREE',
  'PIN_FAILED_ATTEMPT_FOUR',
  'PIN_FAILED_ATTEMPT_FIVE',
  'PIN_FAILED_ATTEMPT_SIX',
  'OTP_FAILED_ATTEMPT_ONE',
  'OTP_FAILED_ATTEMPT_TWO',
  'OTP_FAILED_ATTEMPT_THREE',
  'OTP_FAILED_ATTEMPT_FOUR',
  'OTP_FAILED_ATTEMPT_FIVE',
  'OTP_FAILED_ATTEMPT_SIX',
  'OTP_VALIDATION_FAILED',
]);

/**
 * Copy del success del traspaso entre cuentas (Figma `QV8OodSHqhZbgH1SkaPTQa`, nodo `2874:22896`).
 * Sus labels de montos son propios, no los del cotizador ("Traspasas"/"Recibes"): el diseño dice
 * "Tu contacto recibe", y como el destino es una cuenta propia queda "Tu cuenta recibe".
 */
export const BETWEEN_ACCOUNTS_SUCCESS_FROM_LABEL = 'Tú envías';
export const BETWEEN_ACCOUNTS_SUCCESS_TO_LABEL = 'Tu cuenta recibe';
export const BETWEEN_ACCOUNTS_SUCCESS_TITLE = 'El traspaso fue realizado correctamente';
export const BETWEEN_ACCOUNTS_SUCCESS_DETAILS_TITLE = 'Detalles del envío';
export const BETWEEN_ACCOUNTS_SUCCESS_AVAILABLE_BALANCE_LABEL = 'Saldo disponible';
export const BETWEEN_ACCOUNTS_SUCCESS_DATE_LABEL = 'Fecha';
export const BETWEEN_ACCOUNTS_SUCCESS_DATE_FORMAT = 'D [de] MMMM [de] YYYY';
